"use client";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "./../components/common/InputField";
import VerificationCodeInput from "./../components/common/VerificationCodeInput";
import TopBar from "./../components/common/TopBar";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("email"); // email, verification, newPassword
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSendCode = (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email.trim()) {
      setErrors({ email: "Email is required" });
      return;
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }

    setIsLoading(true);

    // Simulate API call to send verification code
    setTimeout(() => {
      setIsLoading(false);
      setCurrentStep("verification");
      setErrors({});
      // In a real app, this would send a verification code to the user's email
      console.log(`Sending verification code to ${email}`);
    }, 1500);
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();

    // Validate verification code
    if (verificationCode.length !== 6) {
      setErrors({ verificationCode: "Please enter a valid 6-digit code" });
      return;
    }

    setIsLoading(true);

    // Simulate API call to verify code
    setTimeout(() => {
      setIsLoading(false);
      setCurrentStep("newPassword");
      setErrors({});
      // In a real app, this would verify the code with the backend
      console.log(`Verifying code: ${verificationCode}`);
    }, 1500);
  };

  const handleResetPassword = (e) => {
    e.preventDefault();

    const newErrors = {};

    // Validate password
    if (!newPassword) {
      newErrors.newPassword = "Password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    // Simulate API call to reset password
    setTimeout(() => {
      setIsLoading(false);
      // In a real app, this would reset the password in the backend
      console.log(`Resetting password for ${email}`);

      // Navigate to login page after successful password reset
      navigate("/login");
    }, 1500);
  };

  return (
    <main className="flex flex-col items-center w-full min-h-screen bg-white">
      <TopBar />

      <section className="flex flex-col gap-6 items-start mt-64 w-[500px] max-md:mt-32 max-md:max-w-[500px] max-md:w-[90%] max-sm:mt-20 max-sm:w-[95%]">
        <h1 className="w-full text-3xl font-semibold leading-9 text-center text-gray-700 max-sm:text-2xl max-sm:leading-8">
          {currentStep === "email" && "Forgot Password"}
          {currentStep === "verification" && "Verify Your Email"}
          {currentStep === "newPassword" && "Reset Your Password"}
        </h1>

        {currentStep === "email" && (
          <form
            onSubmit={handleSendCode}
            className="flex flex-col gap-4 items-start w-full"
          >
            <p className="text-sm text-gray-600 text-center w-full mb-2">
              Enter your email address and we'll send you a verification code to
              reset your password.
            </p>

            <InputField
              label="Email Address"
              placeholder="Enter your email address"
              type="email"
              value={email}
              onChange={(e) => {
                
                setEmail(e.target.value)
              }}
              error={errors.email}
              required
            />

            <button
              type="submit"
              className={`gap-2 px-6 py-3 w-full text-base font-medium leading-7 text-white bg-rose-700 h-[50px] rounded-[400px] max-sm:h-11 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Code"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="gap-2 px-6 py-3 w-full text-base font-medium leading-7 text-rose-700 border border-rose-700 border-solid h-[50px] rounded-[400px] max-sm:h-11"
            >
              Back to Login
            </button>
          </form>
        )}

        {currentStep === "verification" && (
          <form
            onSubmit={handleVerifyCode}
            className="flex flex-col gap-4 items-start w-full"
          >
            <p className="text-sm text-gray-600 text-center w-full mb-2">
              We've sent a 6-digit verification code to <strong>{email}</strong>
              . Enter the code below to verify your email.
            </p>

            <div className="w-full">
              <label className="gap-1 w-full text-sm font-semibold leading-6 text-gray-700 mb-2 block">
                Verification Code
              </label>
              <VerificationCodeInput
                length={6}
                value={verificationCode}
                onChange={setVerificationCode}
                error={errors.verificationCode}
              />
            </div>

            <button
              type="submit"
              className={`gap-2 px-6 py-3 w-full text-base font-medium leading-7 text-white bg-rose-700 h-[50px] rounded-[400px] max-sm:h-11 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>

            <div className="flex justify-between w-full">
              <button
                type="button"
                onClick={() => setCurrentStep("email")}
                className="text-sm text-rose-700"
              >
                Change Email
              </button>

              <button
                type="button"
                onClick={handleSendCode}
                className="text-sm text-rose-700"
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        {currentStep === "newPassword" && (
          <form
            onSubmit={handleResetPassword}
            className="flex flex-col gap-4 items-start w-full"
          >
            <p className="text-sm text-gray-600 text-center w-full mb-2">
              Create a new password for your account.
            </p>

            <InputField
              label="New Password"
              placeholder="Enter your new password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={errors.newPassword}
              required
            />

            <InputField
              label="Confirm New Password"
              placeholder="Confirm your new password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
              required
            />

            <button
              type="submit"
              className={`gap-2 px-6 py-3 w-full text-base font-medium leading-7 text-white bg-rose-700 h-[50px] rounded-[400px] max-sm:h-11 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Reset Password"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
};

export default ForgotPasswordPage;
